import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Text,
  Flex,
  Input,
  Button,
  useToast,
  Spinner,
  IconButton,
  Textarea,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Switch,
} from '@chakra-ui/react';
import { CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { fetchTags, createTag, updateTag } from '../../api/endpointsBackend';
import { ColorPicker } from '../categories/CategoriesPage';
import MovementsList from '../shared/MovementsList';
import MovementDetailPanel from '../movements/MovementDetailPanel';
import ConvertedTotal from '../shared/ConvertedTotal';
import { useConvertedTotal } from '../../hooks/useConvertedTotal';

function formatTagDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const months = i18n.t('months.short', { ns: 'dates', returnObjects: true });
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function tagPeriodLabel(tag) {
  const start = formatTagDate(tag.start_date);
  const end = formatTagDate(tag.end_date);
  if (!start && !end) return null;
  return `${start || '?'} → ${end || '?'}`;
}

function TagPeriodPill({ tag }) {
  const period = tagPeriodLabel(tag);
  if (!period) return null;
  return (
    <Box
      px={2}
      py="2px"
      borderRadius="full"
      bg="redesign.bg.card"
      border="1px solid"
      borderColor="redesign.border"
      fontSize="2xs"
      fontWeight={500}
      color="redesign.text.muted"
      letterSpacing="wide"
      whiteSpace="nowrap"
      flexShrink={0}
    >
      {period}
    </Box>
  );
}

function CurrencyHeading({ currency }) {
  return (
    <Flex align="center" gap={2} mb={1}>
      <Text fontSize="2xs" fontWeight={600} color="redesign.text.muted" bg="redesign.bg.card" px={2} py="2px" borderRadius="md">
        {currency}
      </Text>
      <Box flex={1} h="1px" bg="redesign.border" />
    </Flex>
  );
}

function Figure({ label, value, currency, strong = false }) {
  return (
    <Flex align="center" py={1}>
      <Text flex={1} fontSize="sm" color={strong ? 'redesign.heading' : 'redesign.text.muted'}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight={strong ? '600' : '400'} color="redesign.heading">
        <Text as="span" fontSize="2xs" mr={1} color="redesign.text.muted">{currency}</Text>
        {value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </Text>
    </Flex>
  );
}

/**
 * Debt progress for a tag used as a loan envelope (MI-316).
 *
 * Three figures, no heading and no bar: borrowed / repaid / outstanding are
 * self-describing, so a title restating them and a bar approximating them both
 * cost vertical space without adding meaning. The scope note stays — that one
 * says something the figures cannot.
 */
function DebtProgress({ selectedRow }) {
  const { t } = useTranslation('tags');
  const currencies = Object.keys(selectedRow.byCurrency)
    .filter((c) => {
      const cur = selectedRow.byCurrency[c];
      return cur.borrowed > 0 || cur.repaid > 0;
    })
    .sort();
  if (currencies.length === 0) return null;
  const showCurrencyHeader = currencies.length > 1;

  return (
    <Box px={{ base: 2, md: 4 }} py={3} borderBottom="1px solid" borderColor="redesign.border">
      {currencies.map((currency, idx) => {
        const c = selectedRow.byCurrency[currency];
        const outstanding = c.iOwe > 0 ? c.iOwe : c.owedToMe;
        const outstandingLabel = c.owedToMe > 0
          ? t('detail.owedToMe')
          : t('detail.outstanding');

        return (
          <Box key={currency} mt={idx === 0 ? 0 : 4}>
            {showCurrencyHeader && <CurrencyHeading currency={currency} />}
            {c.borrowed > 0 && (
              <Figure label={t('detail.borrowed')} value={c.borrowed} currency={currency} />
            )}
            {c.repaid > 0 && (
              <Figure label={t('detail.repaid')} value={c.repaid} currency={currency} />
            )}
            <Figure label={outstandingLabel} value={outstanding} currency={currency} strong />
          </Box>
        );
      })}
    </Box>
  );
}

function SpendingPerAccount({ selectedRow }) {
  const { t } = useTranslation('tags');
  const currencies = Object.keys(selectedRow.byCurrency)
    .filter((c) => (selectedRow.byCurrency[c].byAccount || []).length > 0)
    .sort();
  if (currencies.length === 0) return null;
  const showCurrencyHeader = currencies.length > 1;

  return (
    <Box px={{ base: 2, md: 4 }} py={3} borderBottom="1px solid" borderColor="redesign.border">
      <Text textStyle="microLabel" mb={3}>
        {t('detail.spendingPerAccount')}
      </Text>
      {currencies.map((currency, idx) => {
        const c = selectedRow.byCurrency[currency];
        const total = c.spent;
        return (
          <Box key={currency} mt={idx === 0 ? 0 : 4}>
            {showCurrencyHeader && <CurrencyHeading currency={currency} />}
            {c.byAccount.map((a) => {
              const pct = total > 0 ? (a.spent / total) * 100 : 0;
              return (
                <Flex key={a.accountId} align="center" py={2}>
                  <Flex flex={1} align="center" gap={2} minW={0}>
                    <Box w="8px" h="8px" borderRadius="full" bg={a.color} flexShrink={0} />
                    <Text fontSize="sm" color="redesign.heading" isTruncated>{a.name}</Text>
                  </Flex>
                  <Text w="100px" textAlign="right" fontSize="sm" color="redesign.heading">
                    <Text as="span" fontSize="2xs" mr={1} color="redesign.text.muted">{currency}</Text>
                    {a.spent.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </Text>
                  <Text w="50px" textAlign="right" fontSize="sm" color="redesign.text.muted">
                    {pct.toFixed(0)}%
                  </Text>
                </Flex>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Both blocks can render together for a tag that mixes a loan with ordinary
 * spending; a pure-debt tag shows only the first, a pure-spend tag only the
 * second. Neither renders for an empty tag.
 */
function TagBreakdown({ selectedRow }) {
  const { t } = useTranslation('tags');
  if (!selectedRow || Object.keys(selectedRow.byCurrency || {}).length === 0) {
    return (
      <Box px={{ base: 2, md: 4 }} py={3} borderBottom="1px solid" borderColor="redesign.border">
        <Text fontSize={{ base: 'xs', md: 'sm' }} color="redesign.text.muted">
          {t('detail.noMovementsYet')}
        </Text>
      </Box>
    );
  }
  return (
    <>
      {selectedRow.hasDebt && <DebtProgress selectedRow={selectedRow} />}
      {selectedRow.hasSpend && <SpendingPerAccount selectedRow={selectedRow} />}
    </>
  );
}

export default function TagDetailPanel({ tagId, selectedRow, categories, allTags, selectedCurrency, onClose, onTagUpdated, onDelete }) {
  const { t } = useTranslation('tags');
  const isCreateMode = !tagId;
  const [tag, setTag] = useState(null);
  const [editedTag, setEditedTag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [selectedMovementId, setSelectedMovementId] = useState(null);
  const [overlayDirty, setOverlayDirty] = useState(false);
  const [movementsRefreshKey, setMovementsRefreshKey] = useState(0);
  const [showEditForm, setShowEditForm] = useState(false);
  const toast = useToast();

  const movementsFilters = useMemo(
    () => tagId ? [{ entity: 'Tag', column: 'id', operator: 'in', values: [tagId] }] : null,
    [tagId]
  );

  // MI-298: single approximate total converted to the chosen display currency
  // (all-time, matching the tag's per-currency totals). No-op until one is picked.
  const { data: convertedTotal, loading: convertedLoading, displayCurrency } =
    useConvertedTotal(movementsFilters);

  const handleOverlayMovementUpdated = useCallback(() => {
    setOverlayDirty(true);
  }, []);

  const handleCloseOverlay = useCallback(() => {
    setSelectedMovementId(null);
    if (overlayDirty) {
      setMovementsRefreshKey(k => k + 1);
      setOverlayDirty(false);
      if (onTagUpdated) onTagUpdated();
    }
  }, [overlayDirty, onTagUpdated]);

  useEffect(() => { setSelectedMovementId(null); setOverlayDirty(false); }, [tagId]);

  const fetchTagData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tags = await fetchTags();
      const foundTag = tags.find(tg => tg.id === tagId);

      if (foundTag) {
        setTag(foundTag);
        setEditedTag(foundTag);
      } else {
        setError(t('detail.notFound'));
      }
    } catch (err) {
      console.error('Error fetching tag:', err);
      setError(t('detail.loadError'));
      toast({
        title: t('detail.toasts.errorTitle'),
        description: t('detail.loadError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [tagId, toast, t]);

  // Fetch tag data or initialize for creation
  useEffect(() => {
    setShowEditForm(false);
    if (tagId) {
      fetchTagData();
    } else {
      const emptyTag = { name: '', description: '', start_date: '', end_date: '', is_active: true, color: '' };
      setTag(emptyTag);
      setEditedTag(emptyTag);
      setIsEditingName(true);
      setShowEditForm(true);
    }
  }, [tagId, fetchTagData]);

  const handleSave = async () => {
    setUpdating(true);
    try {
      // Only send non-empty fields
      const dataToSubmit = {};
      if (editedTag.name) dataToSubmit.name = editedTag.name;
      if (editedTag.description) dataToSubmit.description = editedTag.description;
      if (editedTag.start_date) dataToSubmit.start_date = editedTag.start_date;
      if (editedTag.end_date) dataToSubmit.end_date = editedTag.end_date;
      if (editedTag.is_active !== undefined) dataToSubmit.is_active = editedTag.is_active;
      if (editedTag.color !== undefined) dataToSubmit.color = editedTag.color || null;

      if (isCreateMode) {
        await createTag(dataToSubmit);
      } else {
        await updateTag(tagId, dataToSubmit);
      }

      toast({
        title: t('detail.toasts.successTitle'),
        description: isCreateMode ? t('detail.toasts.createSuccess') : t('detail.toasts.updateSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (onTagUpdated) {
        onTagUpdated();
      }

      if (isCreateMode && onClose) {
        onClose();
      } else {
        setShowEditForm(false);
      }
    } catch (err) {
      console.error(isCreateMode ? 'Error creating tag:' : 'Error updating tag:', err);
      toast({
        title: t('detail.toasts.errorTitle'),
        description: isCreateMode ? t('detail.toasts.createError') : t('detail.toasts.updateError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedTag(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteClick = () => {
    if (onDelete && tag) {
      onClose();
      onDelete(tag);
    }
  };

  return (
    <Box
      h="100%"
      bg="redesign.bg.surface"
      borderLeft="1px solid"
      borderColor="redesign.border"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      {selectedMovementId && (
        <Box position="absolute" inset={0} bg="redesign.bg.surface" zIndex={2} display="flex" flexDirection="column">
          <MovementDetailPanel
            movementId={selectedMovementId}
            onClose={handleCloseOverlay}
            onMovementUpdated={handleOverlayMovementUpdated}
            onMovementDeleted={handleOverlayMovementUpdated}
            onSelectMovement={setSelectedMovementId}
          />
        </Box>
      )}
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        px={{ base: 2, md: 4 }}
        py={{ base: 2, md: 3 }}
        borderBottom="1px solid"
        borderColor="redesign.border"
      >
        <Text fontSize="sm" fontWeight="500" color="redesign.text">
          {isCreateMode ? t('detail.headerNew') : t('detail.header')}
        </Text>
        <Flex gap={2} align="center">
          {!isCreateMode && !showEditForm && onTagUpdated && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => { setShowEditForm(true); setEditedTag(tag); }}
            >
              {t('detail.edit')}
            </Button>
          )}
          {!isCreateMode && onDelete && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Text fontSize="lg">⋯</Text>}
                variant="ghost"
                size="sm"
                aria-label={t('detail.options')}
              />
              <MenuList>
                <MenuItem
                  icon={<DeleteIcon />}
                  color="redesign.accent.red"
                  onClick={handleDeleteClick}
                >
                  {t('detail.delete')}
                </MenuItem>
              </MenuList>
            </Menu>
          )}
          <IconButton
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label={t('detail.closeAria')}
          />
        </Flex>
      </Flex>

      {/* Content - Scrollable */}
      <Box flex="1" overflowY="auto" overflowX="hidden" px={{ base: 2, md: 4 }} py={{ base: 2, md: 4 }}>
        {loading && (
          <Flex justify="center" align="center" py={12}>
            <Spinner size="xl" />
          </Flex>
        )}

        {error && (
          <Text color="redesign.accent.red" textAlign="center" py={12}>
            {error}
          </Text>
        )}

        {!loading && !error && editedTag && (
          <Box>
            {/* Name + period pill on the right; "Inactive" label below for inactive tags */}
            <Box mb={{ base: 2, md: 4 }}>
              <Flex justify="space-between" align="center" gap={3}>
                <Box flex="1" minW={0}>
                  {(showEditForm || isCreateMode) && isEditingName ? (
                    <Input
                      value={editedTag.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      onBlur={() => setIsEditingName(false)}
                      autoFocus
                      fontSize={{ base: "lg", md: "2xl" }}
                      fontWeight="600"
                      variant="unstyled"
                      placeholder={t('detail.fields.name')}
                    />
                  ) : (
                    <Text
                      fontSize={{ base: "lg", md: "2xl" }}
                      fontWeight="600"
                      onClick={() => (showEditForm || isCreateMode) && setIsEditingName(true)}
                      cursor={(showEditForm || isCreateMode) ? "pointer" : "default"}
                      _hover={(showEditForm || isCreateMode) ? { color: "redesign.accent.blue" } : undefined}
                    >
                      {editedTag.name || ((showEditForm || isCreateMode) ? t('detail.fields.addName', { defaultValue: 'Click to add name' }) : '')}
                    </Text>
                  )}
                </Box>
                {/* MI-298 total rides the name's row in view mode: it is a
                    single figure and a line of its own left a large gap
                    between the title and the breakdown. */}
                {!isCreateMode && !showEditForm && displayCurrency && (
                  <Flex align="center" flexShrink={0}>
                    <ConvertedTotal
                      data={convertedTotal}
                      loading={convertedLoading}
                      magnitude
                      size="md"
                    />
                  </Flex>
                )}
                {!isCreateMode && <TagPeriodPill tag={editedTag} />}
              </Flex>
              {!isCreateMode && editedTag.is_active === false && (
                <Text
                  mt={1}
                  textStyle="microLabel"
                >
                  {t('detail.inactive')}
                </Text>
              )}
            </Box>

            {/* View mode: spending breakdown + movements */}
            {!isCreateMode && !showEditForm && (
              <>
                <Box mt={{ base: 2, md: 4 }} mx={{ base: -2, md: -4 }}>
                  <TagBreakdown selectedRow={selectedRow} />
                </Box>
                {movementsFilters && (
                  <Box mt={{ base: 2, md: 4 }}>
                    <Text textStyle="microLabel" mb={2}>
                      {t('detail.movements')}
                    </Text>
                    <MovementsList
                      filters={movementsFilters}
                      categories={categories}
                      allTags={allTags}
                      currency={selectedCurrency}
                      showAccountName
                      onMovementClick={setSelectedMovementId}
                      refreshKey={movementsRefreshKey}
                    />
                  </Box>
                )}
              </>
            )}

            {/* Edit mode: editable fields */}
            {(showEditForm || isCreateMode) && (
            <Box mt={{ base: 2, md: 4 }}>
              {/* Status Switch (hidden in create mode) */}
              {!isCreateMode && (
                <Flex py="8px" align="flex-start">
                  <Text fontSize={{ base: "sm", md: "md" }} color="redesign.text.muted" w={{ base: "70px", md: "100px" }} pt={1}>{t('detail.fields.status')}</Text>
                  <Switch
                    size={{ base: "sm", md: "md" }}
                    isChecked={editedTag.is_active}
                    onChange={() => handleFieldChange('is_active', !editedTag.is_active)}
                    aria-label={t('detail.fields.statusAria')}
                  />
                </Flex>
              )}

              <Flex py="8px" align="center">
                <Text fontSize={{ base: "sm", md: "md" }} color="redesign.text.muted" w={{ base: "70px", md: "100px" }}>{t('detail.fields.startDate')}</Text>
                <Input
                  type="date"
                  value={editedTag.start_date || ''}
                  onChange={(e) => handleFieldChange('start_date', e.target.value)}
                  variant="unstyled"
                  fontSize={{ base: "sm", md: "md" }}
                  h="auto"
                  py={0}
                />
              </Flex>

              <Flex py="8px" align="center">
                <Text fontSize={{ base: "sm", md: "md" }} color="redesign.text.muted" w={{ base: "70px", md: "100px" }}>{t('detail.fields.endDate')}</Text>
                <Input
                  type="date"
                  value={editedTag.end_date || ''}
                  onChange={(e) => handleFieldChange('end_date', e.target.value)}
                  variant="unstyled"
                  fontSize={{ base: "sm", md: "md" }}
                  h="auto"
                  py={0}
                />
              </Flex>

              <Flex py="8px" align="center">
                <Text fontSize={{ base: "sm", md: "md" }} color="redesign.text.muted" w={{ base: "70px", md: "100px" }}>{t('detail.fields.color')}</Text>
                <ColorPicker
                  value={editedTag.color || ''}
                  onChange={(color) => handleFieldChange('color', color)}
                />
              </Flex>

              <Box py="8px">
                <Text fontSize={{ base: "sm", md: "md" }} color="redesign.text.muted" mb={1}>{t('detail.fields.description')}</Text>
                {isEditingDescription ? (
                  <Textarea
                    value={editedTag.description || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    onBlur={() => setIsEditingDescription(false)}
                    autoFocus
                    placeholder={t('detail.fields.descriptionPlaceholder')}
                    size="sm"
                    rows={4}
                    resize="vertical"
                    fontSize={{ base: "sm", md: "md" }}
                  />
                ) : (
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="redesign.text"
                    whiteSpace="pre-wrap"
                    onClick={() => setIsEditingDescription(true)}
                    cursor="pointer"
                    _hover={{ color: "redesign.accent.blue" }}
                    minH="40px"
                  >
                    {editedTag.description || t('detail.fields.addDescription')}
                  </Text>
                )}
              </Box>

              {/* Save / Cancel */}
              <Flex mt={{ base: 2, md: 4 }} gap={2}>
                <Button
                  variant="primary"
                  flex={1}
                  size="sm"
                  onClick={handleSave}
                  isLoading={updating}
                  isDisabled={!editedTag?.name?.trim()}
                >
                  {isCreateMode ? t('detail.buttons.create') : t('detail.buttons.save')}
                </Button>
                {!isCreateMode && (
                  <Button variant="outline" size="sm" onClick={() => { setShowEditForm(false); setEditedTag(tag); }}>
                    {t('detail.buttons.cancel')}
                  </Button>
                )}
              </Flex>
            </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
